import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, Payment, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, Print, Search as SearchIcon
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Box, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Fade,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import PaymentReceiptsModal from './PaymentReceiptsModal';
import Pagination from '../../common/Pagination';
import './PaymentReceiptsIndex.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';


const PaymentReceiptsIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();

  const hasCreatePermission = permissions?.includes('Create Master');
  const hasEditPermission = permissions?.includes('Edit Master');
  const hasDeletePermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [masters, setMasters] = useState([]);
  const [party, setParty] = useState([]);
  const [banks, setBanks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [companyData, setCompanyData] = useState(null);

  // Track if masters have been loaded
  const [mastersLoaded, setMastersLoaded] = useState(false);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // payment or receipt
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mounted, setMounted] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Cheque status update modal
  const [chequeStatusModalOpen, setChequeStatusModalOpen] = useState(false);
  const [selectedChequeItem, setSelectedChequeItem] = useState(null);
  const [newChequeStatus, setNewChequeStatus] = useState(1);
  const [bulkChequeStatusModalOpen, setBulkChequeStatusModalOpen] = useState(false);
  const [selectedChequeItems, setSelectedChequeItems] = useState([]);
  const [individualChequeStatuses, setIndividualChequeStatuses] = useState({}); // Store individual status for each item

  // Drill-down states for animated expansion - REMOVED (not needed)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
      fetchMasters();
      fetchBanks();
      fetchPurchases();
      fetchSales();
      fetchPaymentMethods();
      fetchGstRates();
      fetchCompanyData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchPaymentReceipts = async () => {
    try {
      const activeResponse = await fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PaymentAndReceiptApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive payment receipts:', inactiveError);
      }

      setPaymentReceipts([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching payment receipts:', error);
      setPaymentReceipts([]);
    }
  };

  const fetchMasters = async () => {
    // Only fetch if not already loaded
    if (mastersLoaded) {
      console.log('Masters already loaded, skipping fetch');
      return;
    }

    try {
      // Fetch all types of masters to populate the masters list
      const [vendorsRes, customersRes, banksRes, accountHeadsRes] = await Promise.allSettled([
        fetchData('VendorApi', `/${companyId}`, token),
        fetchData('CustomerApi', `/${companyId}`, token),
        fetchData('BankApi', `/${companyId}`, token),
        fetchData('AccountHeadApi', `/${companyId}`, token)
      ]);

      // Combine all masters data
      const vendorsList = vendorsRes.status === 'fulfilled' ? (vendorsRes.value.response_data || vendorsRes.value || []) : [];
      const customersList = customersRes.status === 'fulfilled' ? (customersRes.value.response_data || customersRes.value || []) : [];
      const banksList = banksRes.status === 'fulfilled' ? (banksRes.value.response_data || banksRes.value || []) : [];
      const accountHeadsList = accountHeadsRes.status === 'fulfilled' ? (accountHeadsRes.value.response_data || accountHeadsRes.value || []) : [];

      // Merge all lists into one masters array
      const allMasters = [
        ...vendorsList,
        ...customersList,
        ...banksList,
        ...accountHeadsList
      ];

      setMasters(allMasters);
      setParty(customersList);
      setMastersLoaded(true); // Mark as loaded
      console.log('Masters loaded successfully:', allMasters.length, 'items');
    } catch (error) {
      console.error('Error fetching masters:', error);
      setMasters([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      setBanks(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const fetchPurchases = async (masterId = null) => {
    if (!masterId || masterId === 0) {
      setPurchases([]);
      return;
    }

    try {
      console.log('Fetching purchases for masterId:', masterId);
      const endpoint = `/GetAllPendingPurchase/${companyId}/${masterId}`;
      const response = await fetchData('PaymentAndReceiptApi', endpoint, token);
      const purchaseData = response.response_data || response || [];
      console.log('Fetched purchases:', purchaseData.length, 'items');
      setPurchases(purchaseData);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    }
  };

  const fetchSales = async (masterId = null) => {
    if (!masterId || masterId === 0) {
      setSales([]);
      return;
    }

    try {
      console.log('Fetching sales for masterId:', masterId);
      const endpoint = `/GetAllPendingSales/${companyId}/${masterId}`;
      const response = await fetchData('PaymentAndReceiptApi', endpoint, token);
      const salesData = response.response_data || response || [];
      console.log('Fetched sales:', salesData.length, 'items');
      setSales(salesData);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    }
  };

  // Fetch bills based on master type (Vendor -> Purchases, Customer -> Sales)
  const fetchBillsForMaster = async (masterId, isType, masterType) => {
    if (!masterId || masterId === 0) {
      setPurchases([]);
      setSales([]);
      return;
    }

    // Fetch based on master type:
    // - Vendor (masterType = 1 or 'Vendor') -> fetch Purchases
    // - Customer (masterType = 2 or 'Customer') -> fetch Sales
    // - For other types (Bank, AccountHead), don't fetch any bills
    
    const masterTypeStr = typeof masterType === 'number' 
      ? (masterType === 1 ? 'Vendor' : masterType === 2 ? 'Customer' : masterType === 3 ? 'Bank' : 'AccountHead')
      : masterType;

    if (masterTypeStr === 'Vendor') {
      await fetchPurchases(masterId);
      setSales([]); // Clear sales when fetching purchases
    } else if (masterTypeStr === 'Customer') {
      await fetchSales(masterId);
      setPurchases([]); // Clear purchases when fetching sales
    } else {
      // For Bank and AccountHead, clear both
      setPurchases([]);
      setSales([]);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token);
      const paymentMethodsData = response.response_data || response || [];

      // Debug logging to check API response
      console.log('=== PAYMENT METHODS API RESPONSE ===');
      console.log('Raw response:', response);
      console.log('Payment methods data:', paymentMethodsData);
      console.log('Payment methods structure:');
      paymentMethodsData.forEach((method, index) => {
        console.log(`Method ${index + 1}:`, {
          Id: method.Id,
          Name: method.Name,
          IsActive: method.IsActive,
          FullObject: method
        });
      });
      console.log('=== END PAYMENT METHODS DEBUG ===');

      setPaymentMethods(paymentMethodsData);
    } catch (error) {
      console.error('Error fetching payment methods:', error);

      // Fallback to default payment methods if API fails
      const fallbackMethods = [
        { Id: 1, Name: 'Cash', IsActive: true },
        { Id: 2, Name: 'Cheque', IsActive: true },
        { Id: 3, Name: 'Online Transfer', IsActive: true }
      ];

      console.log('=== USING FALLBACK PAYMENT METHODS ===');
      console.log('Fallback methods:', fallbackMethods);
      console.log('=== END FALLBACK DEBUG ===');

      setPaymentMethods(fallbackMethods);
    }
  };

  const fetchGstRates = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/GetGSTList/${companyId}`, token);
      setGstRates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching GST rates:', error);
      // Fallback to default GST rates if API fails
      setGstRates([
        { Id: 1, Rate: 0, Name: '0%', IsActive: true },
        { Id: 2, Rate: 5, Name: '5%', IsActive: true },
        { Id: 3, Rate: 12, Name: '12%', IsActive: true },
        { Id: 4, Rate: 18, Name: '18%', IsActive: true },
        { Id: 5, Rate: 28, Name: '28%', IsActive: true }
      ]);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      setCompanyData(response.response_data || response || null);
    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyData(null);
    }
  };

  const fetchMaxBillNo = async (isType = true) => {
    try {
      const isTypeString = isType.toString();
      const response = await fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId || 1}/${companyId}/${isTypeString}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching payment receipts...');
    try {
      await Promise.allSettled([
        fetchPaymentReceipts(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific payment receipt details by ID
  const fetchPaymentReceiptDetails = async (paymentReceiptId) => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${paymentReceiptId}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error('Error fetching payment receipt details:', error);
      // Try alternative endpoint if GetById doesn't work
      try {
        const altResponse = await fetchData('PaymentAndReceiptApi', `/${paymentReceiptId}`, token);
        return altResponse.response_data || altResponse || null;
      } catch (altError) {
        console.error('Error with alternative endpoint:', altError);
        return null;
      }
    }
  };

  // Print Payment/Receipt handler - Professional jsPDF Format
  const handlePrintPaymentReceipt = async (paymentReceiptId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');

    try {
      const freshData = await fetchPaymentReceiptDetails(paymentReceiptId);

      if (!freshData) {
        setError('Failed to fetch payment receipt details for printing.');
        setLoading(false);
        return;
      }

      const paymentData = freshData.PaymentAndReceipt || freshData;
      const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
      const saleDetails = freshData.PaymentAndReceiptSale || [];

      // Get master details
      const master = masters.find(m => m.Id === paymentData.MasterId);
      const masterName = paymentData.MasterName || master?.Name || '-';
      const masterAddress = master?.Address || '';
      const masterGST = master?.GSTNumber || '';
      const masterContact = master?.ContactNumber || master?.Mobile || '';

      // Get bank details if applicable
      const bank = banks.find(b => b.Id === paymentData.BankId);
      const bankName = bank?.Name || '';

      // Get payment method
      const paymentMethod = paymentMethods.find(pm => pm.Id === paymentData.PaymentType);
      const paymentMethodName = paymentMethod?.Name || 'Cash';

      // Company details
      const companyNameDisplay = companyData?.Name || 'Company Name';
      const companyAddress = companyData?.Address || '';
      const companyGST = companyData?.GSTNumber || '';
      const companyContact = companyData?.ContactNumber || '';
      const companyEmail = companyData?.Email || '';

      const isPayment = paymentData.IsType;
      const documentType = isPayment ? 'PAYMENT VOUCHER' : 'RECEIPT VOUCHER';
      const partyLabel = isPayment ? 'Paid To' : 'Received From';

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 15;
      let yPosition = margin;

      // Enhanced color scheme
      const primaryColor = isPayment ? [220, 38, 127] : [16, 185, 129]; // Pink for Payment, Green for Receipt
      const secondaryColor = isPayment ? [190, 24, 93] : [5, 150, 105];
      const accentColor = [59, 130, 246]; // Blue accent
      const textColor = [17, 24, 39];
      const lightGray = [248, 250, 252];
      const mediumGray = [148, 163, 184];
      const darkGray = [71, 85, 105];

      // Helper function to add styled text
      const addStyledText = (text, x, y, options = {}) => {
        const {
          fontSize = 10,
          fontStyle = 'normal',
          color = textColor,
          align = 'left',
          maxWidth = null
        } = options;

        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);

        if (maxWidth) {
          const lines = pdf.splitTextToSize(text, maxWidth);
          pdf.text(lines, x, y, { align });
          return y + (lines.length * fontSize * 0.35);
        } else {
          pdf.text(text, x, y, { align });
          return y + (fontSize * 0.35);
        }
      };

      // Helper function to add rectangle with border
      const addStyledRect = (x, y, width, height, options = {}) => {
        const {
          fillColor = null,
          borderColor = [0, 0, 0],
          borderWidth = 0.5
        } = options;

        if (fillColor) {
          pdf.setFillColor(...fillColor);
          pdf.rect(x, y, width, height, 'F');
        }

        pdf.setDrawColor(...borderColor);
        pdf.setLineWidth(borderWidth);
        pdf.rect(x, y, width, height, 'S');
      };

      // Company Header with styling
      addStyledRect(margin, yPosition, pageWidth - 2 * margin, 25, {
        fillColor: lightGray,
        borderColor: primaryColor,
        borderWidth: 1
      });

      yPosition += 5;
      yPosition = addStyledText(companyNameDisplay, pageWidth / 2, yPosition, {
        fontSize: 16,
        fontStyle: 'bold',
        color: primaryColor,
        align: 'center'
      });

      if (companyAddress) {
        yPosition = addStyledText(companyAddress, pageWidth / 2, yPosition + 3, {
          fontSize: 9,
          align: 'center',
          maxWidth: pageWidth - 2 * margin - 10
        });
      }

      const contactInfo = [
        companyGST ? `GST: ${companyGST}` : '',
        companyContact ? `Tel: ${companyContact}` : '',
        companyEmail ? `Email: ${companyEmail}` : ''
      ].filter(Boolean).join(' | ');

      if (contactInfo) {
        yPosition = addStyledText(contactInfo, pageWidth / 2, yPosition + 2, {
          fontSize: 8,
          align: 'center'
        });
      }

      yPosition += 10;

      // Document Title with colored background
      addStyledRect(margin, yPosition, pageWidth - 2 * margin, 12, {
        fillColor: primaryColor,
        borderColor: primaryColor,
        borderWidth: 1
      });

      yPosition = addStyledText(documentType, pageWidth / 2, yPosition + 8, {
        fontSize: 14,
        fontStyle: 'bold',
        color: [255, 255, 255],
        align: 'center'
      });

      yPosition += 8;

      // Voucher Information Section
      const leftColWidth = (pageWidth - 2 * margin) * 0.65;
      const rightColWidth = (pageWidth - 2 * margin) * 0.35;

      // Party Information Box
      addStyledRect(margin, yPosition, leftColWidth, 35, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let partyYPos = yPosition + 5;
      partyYPos = addStyledText(partyLabel, margin + 5, partyYPos, {
        fontSize: 10,
        fontStyle: 'bold',
        color: primaryColor
      });

      partyYPos = addStyledText(masterName, margin + 15, partyYPos + 3, {
        fontSize: 11,
        fontStyle: 'bold'
      });

      if (masterAddress) {
        partyYPos = addStyledText(masterAddress, margin + 15, partyYPos + 2, {
          fontSize: 9,
          maxWidth: leftColWidth - 25
        });
      }

      if (masterGST) {
        partyYPos = addStyledText(`GST: ${masterGST}`, margin + 15, partyYPos + 2, {
          fontSize: 9
        });
      }

      if (masterContact) {
        addStyledText(`Contact: ${masterContact}`, margin + 15, partyYPos + 2, {
          fontSize: 9
        });
      }

      // Voucher Details Box
      addStyledRect(margin + leftColWidth, yPosition, rightColWidth, 35, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let detailsYPos = yPosition + 5;
      const detailsX = margin + leftColWidth + 5;

      detailsYPos = addStyledText(`Voucher No: ${paymentData.BillNumber}`, detailsX, detailsYPos, {
        fontSize: 9,
        fontStyle: 'bold'
      });

      detailsYPos = addStyledText(`Date: ${formatDate(paymentData.BillDate)}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      detailsYPos = addStyledText(`FY: ${paymentData.FinYear || new Date().getFullYear()}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      detailsYPos = addStyledText(`Mode: ${paymentMethodName}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      if (paymentData.ChequeNumber) {
        detailsYPos = addStyledText(`Ref: ${paymentData.ChequeNumber}`, detailsX, detailsYPos + 3, {
          fontSize: 9
        });
      }

      if (bankName) {
        addStyledText(`Bank: ${bankName}`, detailsX, detailsYPos + 3, {
          fontSize: 9
        });
      }

      yPosition += 45;

      // Transaction Details Table
      const tableData = [];
      let totalAmount = parseFloat(paymentData.Amount) || 0;

      if (isPayment && purchaseDetails.length > 0) {
        purchaseDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Purchase Bill Payment',
            `Purchase ID: ${d.PurchaseId || '-'}`,
            formatCurrency(d.Amount || 0)
          ]);
        });
      } else if (!isPayment && saleDetails.length > 0) {
        saleDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Sale Bill Receipt',
            `Sale ID: ${d.SaleId || '-'}`,
            formatCurrency(d.Amount || 0)
          ]);
        });
      } else {
        tableData.push([
          '1',
          isPayment ? 'General Payment' : 'General Receipt',
          paymentData.Remarks || `Direct ${isPayment ? 'Payment' : 'Receipt'}`,
          formatCurrency(totalAmount)
        ]);
      }

      // Create table with autoTable
      if (typeof pdf.autoTable === 'function') {
        pdf.autoTable({
          startY: yPosition,
          head: [['Sr.No', 'Particulars', 'Description', 'Amount']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontSize: 11,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 4
          },
          bodyStyles: {
            fontSize: 10,
            textColor: textColor,
            cellPadding: 3
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 20 },
            1: { halign: 'left', cellWidth: 50 },
            2: { halign: 'left', cellWidth: 75 },
            3: { halign: 'right', cellWidth: 30, fontStyle: 'bold' }
          },
          margin: { left: margin, right: margin },
          tableLineColor: primaryColor,
          tableLineWidth: 0.8,
          alternateRowStyles: {
            fillColor: [248, 250, 252]
          }
        });

        yPosition = pdf.lastAutoTable ? pdf.lastAutoTable.finalY + 10 : yPosition + 100;
      } else {
        console.error('autoTable plugin not loaded properly');
        yPosition += 100; // Fallback positioning
      }


      // Amount Summary Section
      const summaryBoxHeight = 40;
      const leftSummaryWidth = (pageWidth - 2 * margin) * 0.6;
      const rightSummaryWidth = (pageWidth - 2 * margin) * 0.4;

      // Amount in Words Box
      addStyledRect(margin, yPosition, leftSummaryWidth, summaryBoxHeight, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let wordsYPos = yPosition + 5;
      wordsYPos = addStyledText('Amount in Words:', margin + 5, wordsYPos, {
        fontSize: 10,
        fontStyle: 'bold',
        color: primaryColor
      });

      wordsYPos = addStyledText(`Rupees ${numberToWords(totalAmount)} Only`, margin + 5, wordsYPos + 3, {
        fontSize: 9,
        fontStyle: 'italic',
        maxWidth: leftSummaryWidth - 10
      });

      if (paymentData.Remarks) {
        wordsYPos = addStyledText('Remarks:', margin + 5, wordsYPos + 5, {
          fontSize: 9,
          fontStyle: 'bold'
        });
        addStyledText(paymentData.Remarks, margin + 5, wordsYPos + 2, {
          fontSize: 8,
          maxWidth: leftSummaryWidth - 10
        });
      }

      // Amount Breakdown Box
      addStyledRect(margin + leftSummaryWidth, yPosition, rightSummaryWidth, summaryBoxHeight, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let amountYPos = yPosition + 5;
      const amountX = margin + leftSummaryWidth + 5;

      // Amount breakdown table
      const amountData = [
        ['Basic Amount:', formatCurrency(paymentData.Amount || 0)]
      ];

      if (paymentData.CGSTAmount > 0) {
        amountData.push(['CGST:', formatCurrency(paymentData.CGSTAmount)]);
      }
      if (paymentData.SGSTAmount > 0) {
        amountData.push(['SGST:', formatCurrency(paymentData.SGSTAmount)]);
      }
      if (paymentData.IGSTAmount > 0) {
        amountData.push(['IGST:', formatCurrency(paymentData.IGSTAmount)]);
      }
      if (paymentData.RoundOff !== 0) {
        amountData.push(['Round Off:', formatCurrency(paymentData.RoundOff || 0)]);
      }

      amountData.forEach(([label, amount]) => {
        addStyledText(label, amountX, amountYPos, { fontSize: 9 });
        addStyledText(amount, amountX + rightSummaryWidth - 15, amountYPos, {
          fontSize: 9,
          align: 'right'
        });
        amountYPos += 4;
      });

      // Total Amount with highlight
      addStyledRect(amountX - 2, amountYPos - 1, rightSummaryWidth - 6, 6, {
        fillColor: primaryColor
      });

      addStyledText('Total Amount:', amountX, amountYPos + 2, {
        fontSize: 10,
        fontStyle: 'bold',
        color: [255, 255, 255]
      });
      addStyledText(formatCurrency(paymentData.TotalAmount || totalAmount), amountX + rightSummaryWidth - 15, amountYPos + 2, {
        fontSize: 10,
        fontStyle: 'bold',
        color: [255, 255, 255],
        align: 'right'
      });

      yPosition += summaryBoxHeight + 15;

      // Signature Section
      const signatureHeight = 25;
      const signatureWidth = (pageWidth - 2 * margin) / 3;

      ['PREPARED BY', 'CHECKED BY', 'AUTHORIZED SIGNATORY'].forEach((title, index) => {
        const x = margin + (index * signatureWidth);
        addStyledRect(x, yPosition, signatureWidth, signatureHeight, {
          fillColor: [255, 255, 255],
          borderColor: primaryColor,
          borderWidth: 0.8
        });

        addStyledText(title, x + signatureWidth / 2, yPosition + signatureHeight - 3, {
          fontSize: 9,
          fontStyle: 'bold',
          align: 'center'
        });
      });

      yPosition += signatureHeight + 10;

      // Footer
      addStyledText('This is a computer generated document and does not require signature.', pageWidth / 2, yPosition, {
        fontSize: 8,
        color: darkGray,
        align: 'center'
      });

      // Add watermark
      pdf.setTextColor(200, 200, 200);
      pdf.setFontSize(60);
      pdf.text(isPayment ? 'PAYMENT' : 'RECEIPT', pageWidth / 2, pageHeight / 2, {
        align: 'center',
        angle: 45
      });

      // Direct Print instead of download
      pdf.autoPrint();

      // Open in new window for printing
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          // Clean up the URL after printing
          setTimeout(() => {
            URL.revokeObjectURL(pdfUrl);
          }, 1000);
        };
      } else {
        // Fallback if popup is blocked
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${documentType.replace(' ', '_')}_${paymentData.BillNumber}_${formatDate(paymentData.BillDate).replace(/\//g, '-')}.pdf`;
        link.click();
        URL.revokeObjectURL(pdfUrl);
      }

      setSuccess(`Document prepared for printing: ${documentType} ${paymentData.BillNumber}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF.');
    }

    setLoading(false);
  };

  // Helper function to convert number to words (basic implementation)
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    let result = '';
    const crores = Math.floor(num / 10000000);
    if (crores > 0) {
      result += convertHundreds(crores) + 'Crore ';
      num %= 10000000;
    }

    const lakhs = Math.floor(num / 100000);
    if (lakhs > 0) {
      result += convertHundreds(lakhs) + 'Lakh ';
      num %= 100000;
    }

    const thousands = Math.floor(num / 1000);
    if (thousands > 0) {
      result += convertHundreds(thousands) + 'Thousand ';
      num %= 1000;
    }

    if (num > 0) {
      result += convertHundreds(num);
    }

    return result.trim();
  };

  // Handle modal actions
  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    setSelectedItem(item);

    if (action === 'create') {
      const defaultIsType = true; // Default to Payment, can be changed in modal
      const maxBillData = await fetchMaxBillNo(defaultIsType);
      const billNumber = maxBillData.billPrefix && maxBillData.finYear ?
        `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` :
        maxBillData.billNumber.toString();

      setFormData({
        Id: 0,
        CompanyId: companyId,
        BranchId: branchId,
        BillNumber: billNumber,
        BillDate: new Date().toISOString().split('T')[0],
        MasterId: '',
        MasterType: 4, // Default to AccountHead (4)
        CustomerId: null,
        VendorId: null,
        AccountHeadId: null,
        BankId: null,
        SubHeadId: '',
        PartyId: '',
        IsType: true, // true for Payment, false for Receipt
        IsGST: false,
        IsIGST: false, // Add IGST field
        GSTHeadId: '',
        IsSaleAndPurchase: false,
        PaymentType: 1, // 1=Cash, 2=Cheque, 3=Online
        ChequeDate: '',
        ChequeNumber: '',
        chequeDetails: '',
        ChequeStatusId: 1,
        Amount: 0,
        GSTRate: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        Remarks: '',
        IsActive: true
      });
      setPaymentReceiptDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        // Fetch complete payment receipt data from API - following JournalEntry pattern
        console.log('Fetching payment receipt data for ID:', item.Id);
        const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${item.Id}`, token);
        const paymentReceiptData = response.response_data || response;
        console.log('Fetched payment receipt data:', paymentReceiptData);

        const paymentData = paymentReceiptData.PaymentAndReceipt;

        setFormData({
          ...paymentData,
          BillDate: paymentData.BillDate ? paymentData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          ChequeDate: paymentData.ChequeDate ? paymentData.ChequeDate.split('T')[0] : '',
          MasterId: paymentData.MasterId || '',
          MasterType: paymentData.MasterType || 4 // Ensure MasterType is set, default to 4 (AccountHead)
        });

        // Set payment receipt details from the API response
        const purchaseDetails = paymentReceiptData.PaymentAndReceiptPurchase || [];
        const saleDetails = paymentReceiptData.PaymentAndReceiptSale || [];

        if (paymentData.IsType && purchaseDetails.length > 0) {
          // Payment - show purchase details
          const mappedDetails = purchaseDetails.map(p => ({
            Id: p.PurchaseId,
            Description: p.PurchaseDescription || `Purchase - ${p.PurchaseId}`,
            Amount: p.PurchaseAmount || 0,
            PaidAmount: p.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: p.BillDate || paymentData.BillDate,
            Type: 'Purchase',
            PaymentAmount: p.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else if (!paymentData.IsType && saleDetails.length > 0) {
          // Receipt - show sale details
          const mappedDetails = saleDetails.map(s => ({
            Id: s.SaleId,
            Description: s.SaleDescription || `Sale - ${s.SaleId}`,
            Amount: s.SaleAmount || 0,
            PaidAmount: s.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: s.BillDate || paymentData.BillDate,
            Type: 'Sale',
            PaymentAmount: s.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else {
          // No specific purchase/sale details - create a general entry
          setPaymentReceiptDetails([{
            Id: paymentData.Id,
            Description: `${paymentData.IsType ? 'Payment' : 'Receipt'} - ${paymentData.BillNumber}`,
            Amount: paymentData.Amount,
            PaidAmount: paymentData.Amount,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: paymentData.BillDate,
            Type: paymentData.IsType ? 'Payment' : 'Receipt',
            PaymentAmount: paymentData.Amount,
            IsSelected: true,
            isDirectPayment: true
          }]);
        }

      } catch (error) {
        console.error('Error fetching payment receipt details:', error);
        setError('Failed to fetch payment receipt details.');
        // Fallback to using the item data from the list
        setFormData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          ChequeDate: item.ChequeDate ? item.ChequeDate.split('T')[0] : '',
          MasterId: item.MasterId || ''
        });

        // Create a basic entry for fallback
        setPaymentReceiptDetails([{
          Id: item.Id,
          Description: `${item.IsType ? 'Payment' : 'Receipt'} - ${item.BillNumber}`,
          Amount: item.Amount,
          PaidAmount: item.Amount,
          BalanceAmount: 0,
          PendingAmount: 0,
          BillDate: item.BillDate,
          Type: item.IsType ? 'Payment' : 'Receipt',
          PaymentAmount: item.Amount,
          IsSelected: true,
          isDirectPayment: true
        }]);
      }
      setModalLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
    setFormData({});
    setPaymentReceiptDetails([]);
  };

  // Handle save
  const handleSave = async () => {
    setModalLoading(true);
    try {
      const dataToSend = {
        PaymentAndReceipt: {
          Id: modalAction === 'edit' ? formData.Id : 0,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: parseInt(formData.MasterId),
          MasterType: formData.MasterType || 4, // Include MasterType (default to 4 for AccountHead)
          CustomerId: formData.CustomerId ? parseInt(formData.CustomerId) : null,
          VendorId: formData.VendorId ? parseInt(formData.VendorId) : null,
          AccountHeadId: formData.AccountHeadId ? parseInt(formData.AccountHeadId) : null,
          BankId: formData.BankId || null,
          SubHeadId: formData.SubHeadId || 0,
          PartyId: formData.PartyId || formData.MasterId, // Use MasterId as PartyId if PartyId not set
          BillDate: formData.BillDate,
          BillNumber: parseInt(formData.BillNumber) || 0,
          FinYear: formData.FinYear || new Date().getFullYear().toString(),
          IsType: formData.IsType, // true for Payment, false for Receipt
          IsGST: formData.IsGST || false,
          GSTHeadId: formData.GSTHeadId || 0,
          IsSaleAndPurchase: formData.IsSaleAndPurchase || true,
          PaymentType: formData.PaymentType || 1, // 1=Cash, 2=Cheque, etc.
          ChequeDate: formData.ChequeDate || null,
          ChequeNumber: formData.ChequeNumber || null,
          chequeDetails: formData.chequeDetails || null,
          ChequeStatusId: formData.ChequeStatusId || 1,
          Amount: parseFloat(formData.Amount) || 0,
          GSTRate: parseFloat(formData.GSTRate) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          RoundOff: parseFloat(formData.RoundOff) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || parseFloat(formData.Amount) || 0,
          Remarks: formData.Remarks || null,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : formData.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }
      };

      // Add purchase/sale details if any are selected
      const selectedDetails = paymentReceiptDetails.filter(d => d.IsSelected && d.PaymentAmount > 0);
      if (selectedDetails.length > 0) {
        if (formData.IsType) {
          // Payment - add purchase details
          dataToSend.PaymentAndReceiptPurchase = selectedDetails.map(d => ({
            Id: 0,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: parseInt(formData.MasterId),
            PurchaseId: d.Id,
            PaymentAndReceiptId: 0, // Will be set by backend after PaymentAndReceipt is created
            Amount: parseFloat(d.PaymentAmount) || 0,
            IsActive: true,
            CreatedBy: userId,
            UpdatedBy: null,
            CreatedDate: new Date().toISOString(),
            UpdatedDate: null
          }));
        } else {
          // Receipt - add sale details
          dataToSend.PaymentAndReceiptSale = selectedDetails.map(d => ({
            Id: 0,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: parseInt(formData.MasterId),
            SaleId: d.Id,
            PaymentAndReceiptId: 0, // Will be set by backend after PaymentAndReceipt is created
            Amount: parseFloat(d.PaymentAmount) || 0,
            IsActive: true,
            CreatedBy: userId,
            UpdatedBy: null,
            CreatedDate: new Date().toISOString(),
            UpdatedDate: null
          }));
        }
      }

      console.log('Saving Payment/Receipt data:', dataToSend);

      if (modalAction === 'create') {
        await postData('PaymentAndReceiptApi', dataToSend, token);
        setSuccess(`${formData.IsType ? 'Payment' : 'Receipt'} created successfully!`);
      } else {
        await updateData('PaymentAndReceiptApi', formData.Id, dataToSend, token);
        setSuccess(`${formData.IsType ? 'Payment' : 'Receipt'} updated successfully!`);
      }

      handleCloseModal();
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error saving payment receipt:', error);
      setError(`Failed to save ${formData.IsType ? 'payment' : 'receipt'}.`);
    }
    setModalLoading(false);
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteData('PaymentAndReceiptApi', deleteItemId, token);
      setSuccess('Payment/Receipt deleted successfully!');
      setDeleteModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error deleting payment receipt:', error);
      setError('Failed to delete payment receipt.');
    }
  };

  // Handle activate
  const handleActivate = async () => {
    try {
      await activateData('PaymentAndReceiptApi', activateItemId, token);
      setSuccess('Payment/Receipt activated successfully!');
      setActivateModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error activating payment receipt:', error);
      setError('Failed to activate payment receipt.');
    }
  };

  // Wrapper functions for new UI
  const handleDeleteConfirm = async () => {
    await handleDelete();
  };

  const handleActivateConfirm = async () => {
    await handleActivate();
  };

  const handleSavePaymentReceipt = async () => {
    await fetchPaymentReceipts();
    setOpenModal(false);
    setSelectedItem(null);
  };

  // Handle cheque status update
  const handleUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque status...');

      const updateData = {
        PaymentAndReceipt: {
          Id: selectedChequeItem.Id,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: selectedChequeItem.MasterId,
          SubHeadId: selectedChequeItem.SubHeadId || 0,
          PartyId: selectedChequeItem.PartyId || selectedChequeItem.MasterId,
          BillDate: selectedChequeItem.BillDate,
          BillNumber: selectedChequeItem.BillNumber,
          FinYear: selectedChequeItem.FinYear || new Date().getFullYear().toString(),
          IsType: selectedChequeItem.IsType,
          IsGST: selectedChequeItem.IsGST || false,
          GSTHeadId: selectedChequeItem.GSTHeadId || 0,
          IsSaleAndPurchase: selectedChequeItem.IsSaleAndPurchase || true,
          PaymentType: selectedChequeItem.PaymentType,
          ChequeDate: selectedChequeItem.ChequeDate,
          ChequeNumber: selectedChequeItem.ChequeNumber,
          BankId: selectedChequeItem.BankId,
          chequeDetails: selectedChequeItem.chequeDetails,
          ChequeStatusId: newChequeStatus, // This is the key update
          Amount: selectedChequeItem.Amount,
          GSTRate: selectedChequeItem.GSTRate || 0,
          CGSTAmount: selectedChequeItem.CGSTAmount || 0,
          SGSTAmount: selectedChequeItem.SGSTAmount || 0,
          IGSTAmount: selectedChequeItem.IGSTAmount || 0,
          RoundOff: selectedChequeItem.RoundOff || 0,
          TotalAmount: selectedChequeItem.TotalAmount,
          Remarks: selectedChequeItem.Remarks,
          IsActive: selectedChequeItem.IsActive,
          CreatedBy: selectedChequeItem.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: selectedChequeItem.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', selectedChequeItem.Id, updateData, token);

      const statusText = newChequeStatus === 1 ? 'Pending' :
        newChequeStatus === 2 ? 'Passed' :
          newChequeStatus === 3 ? 'Bounced' : 'Cancelled';

      setSuccess(`Cheque status updated to "${statusText}" successfully!`);
      setChequeStatusModalOpen(false);
      setSelectedChequeItem(null);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque status:', error);
      setError('Failed to update cheque status.');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk cheque status update with individual statuses
  const handleBulkUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque statuses...');

      const updatePromises = selectedChequeItems.map(async (item) => {
        const individualStatus = individualChequeStatuses[item.Id] || newChequeStatus;

        const updatePayload = {
          PaymentAndReceipt: {
            Id: item.Id,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: item.MasterId,
            SubHeadId: item.SubHeadId || 0,
            PartyId: item.PartyId || item.MasterId,
            BillDate: item.BillDate,
            BillNumber: item.BillNumber,
            FinYear: item.FinYear || new Date().getFullYear().toString(),
            IsType: item.IsType,
            IsGST: item.IsGST || false,
            GSTHeadId: item.GSTHeadId || 0,
            IsSaleAndPurchase: item.IsSaleAndPurchase || true,
            PaymentType: item.PaymentType,
            ChequeDate: item.ChequeDate,
            ChequeNumber: item.ChequeNumber,
            BankId: item.BankId,
            chequeDetails: item.chequeDetails,
            ChequeStatusId: individualStatus, // Use individual status for each item
            Amount: item.Amount,
            GSTRate: item.GSTRate || 0,
            CGSTAmount: item.CGSTAmount || 0,
            SGSTAmount: item.SGSTAmount || 0,
            IGSTAmount: item.IGSTAmount || 0,
            RoundOff: item.RoundOff || 0,
            TotalAmount: item.TotalAmount,
            Remarks: item.Remarks,
            IsActive: item.IsActive,
            CreatedBy: item.CreatedBy,
            UpdatedBy: userId,
            CreatedDate: item.CreatedDate,
            UpdatedDate: new Date().toISOString()
          }
        };

        return updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);
      });

      await Promise.all(updatePromises);

      setSuccess(`${selectedChequeItems.length} cheque statuses updated successfully!`);
      setBulkChequeStatusModalOpen(false);
      setSelectedChequeItems([]);
      setIndividualChequeStatuses({});
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque statuses:', error);
      setError('Failed to update cheque statuses.');
    } finally {
      setLoading(false);
    }
  };

  // Open cheque status update modal
  const handleOpenChequeStatusModal = (item) => {
    setSelectedChequeItem(item);
    setNewChequeStatus(item.ChequeStatusId || 1);
    setChequeStatusModalOpen(true);
  };

  // Get cheque payment method ID dynamically
  const getChequePaymentMethodId = () => {
    console.log('=== GETTING CHEQUE PAYMENT METHOD ID ===');
    console.log('Available payment methods:', paymentMethods);

    const chequeMethod = paymentMethods.find(pm =>
      pm.Name && pm.Name.toLowerCase().includes('cheque')
    );

    console.log('Found cheque method:', chequeMethod);

    const chequeId = chequeMethod ? chequeMethod.Id : 2;
    console.log('Using cheque payment method ID:', chequeId);
    console.log('=== END CHEQUE METHOD DEBUG ===');

    return chequeId;
  };

  // Handle individual cheque status update (single item)
  const handleIndividualChequeStatusUpdate = async (item, newStatus) => {
    try {
      setLoading(true);
      setLoadingMessage(`Updating cheque status for ${item.BillNumber}...`);

      const updatePayload = {
        PaymentAndReceipt: {
          Id: item.Id,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: item.MasterId,
          SubHeadId: item.SubHeadId || 0,
          PartyId: item.PartyId || item.MasterId,
          BillDate: item.BillDate,
          BillNumber: item.BillNumber,
          FinYear: item.FinYear || new Date().getFullYear().toString(),
          IsType: item.IsType,
          IsGST: item.IsGST || false,
          GSTHeadId: item.GSTHeadId || 0,
          IsSaleAndPurchase: item.IsSaleAndPurchase || true,
          PaymentType: item.PaymentType,
          ChequeDate: item.ChequeDate,
          ChequeNumber: item.ChequeNumber,
          BankId: item.BankId,
          chequeDetails: item.chequeDetails,
          ChequeStatusId: newStatus,
          Amount: item.Amount,
          GSTRate: item.GSTRate || 0,
          CGSTAmount: item.CGSTAmount || 0,
          SGSTAmount: item.SGSTAmount || 0,
          IGSTAmount: item.IGSTAmount || 0,
          RoundOff: item.RoundOff || 0,
          TotalAmount: item.TotalAmount,
          Remarks: item.Remarks,
          IsActive: item.IsActive,
          CreatedBy: item.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: item.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);

      const statusText = newStatus === 1 ? 'Pending' :
        newStatus === 2 ? 'Passed' :
          newStatus === 3 ? 'Bounced' : 'Cancelled';

      setSuccess(`Cheque status updated to "${statusText}" for ${item.BillNumber}!`);

      // Update the individual status in state
      setIndividualChequeStatuses(prev => ({
        ...prev,
        [item.Id]: newStatus
      }));

      // Refresh the main data
      await fetchPaymentReceipts();

    } catch (error) {
      console.error('Error updating individual cheque status:', error);
      setError(`Failed to update cheque status for ${item.BillNumber}.`);
    } finally {
      setLoading(false);
    }
  };

  // Handle individual cheque status change
  const handleIndividualStatusChange = (itemId, newStatus) => {
    setIndividualChequeStatuses(prev => ({
      ...prev,
      [itemId]: newStatus
    }));
  };

  // Get status color and text
  const getStatusInfo = (statusId) => {
    switch (statusId) {
      case 1:
        return { text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' };
      case 2:
        return { text: 'Passed', color: '#10b981', bgColor: '#d1fae5', icon: '✅' };
      case 3:
        return { text: 'Bounced', color: '#ef4444', bgColor: '#fee2e2', icon: '❌' };
      case 4:
        return { text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' };
      default:
        return { text: 'Unknown', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
    }
  };

  // Open bulk cheque status update modal
  const handleOpenBulkChequeStatusModal = () => {
    console.log('=== OPENING BULK CHEQUE STATUS MODAL ===');

    const chequePaymentMethodId = getChequePaymentMethodId();
    console.log('Filtering items with PaymentType:', chequePaymentMethodId);
    console.log('Total filtered data items:', filteredData.length);

    const chequeItems = filteredData.filter(item => {
      const isPaymentTypeMatch = item.PaymentType === chequePaymentMethodId;
      const isActive = item.IsActive;
      const isStatusUpdateable = item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1;
      const isMatch = isPaymentTypeMatch && isActive && isStatusUpdateable;

      console.log(`Item ${item.Id} (${item.BillNumber}): PaymentType=${item.PaymentType}, IsActive=${item.IsActive}, ChequeStatusId=${item.ChequeStatusId}, Updateable=${isStatusUpdateable}, Match=${isMatch}`);
      return isMatch;
    });

    console.log('Found cheque items:', chequeItems.length);
    console.log('Cheque items details:', chequeItems);

    if (chequeItems.length === 0) {
      console.log('No updateable cheque items found - showing error');
      setError('No pending cheque payments found to update. Only cheques with Pending status can be updated.');
      return;
    }

    setSelectedChequeItems(chequeItems);
    setNewChequeStatus(2); // Default to "Passed"

    // Initialize individual statuses with default value
    const initialStatuses = {};
    chequeItems.forEach(item => {
      initialStatuses[item.Id] = item.ChequeStatusId || 1; // Use current status or default to "Pending"
    });
    setIndividualChequeStatuses(initialStatuses);

    console.log('Initial statuses:', initialStatuses);
    console.log('=== END MODAL OPENING DEBUG ===');

    setBulkChequeStatusModalOpen(true);
  };

  // Filter and search logic
  const filteredData = paymentReceipts.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);

    const matchesType = filterType === 'all' ||
      (filterType === 'payment' && item.IsType) ||
      (filterType === 'receipt' && !item.IsType);

    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPaymentTypeText = (paymentType) => {
    const paymentMethod = paymentMethods.find(pm => pm.Id === paymentType);
    return paymentMethod ? paymentMethod.Name : 'Cash';
  };

  // Handle expand row functionality - REMOVED (not needed)

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 9999,
              backdropFilter: 'blur(4px)'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: "#0d766e",
                  mb: 2
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "#0d766e",
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                Loading Payment & Receipts...
              </Typography>
            </Box>
          </Box>
        )}

        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}</style>

        <div className="suite-page-panel" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: "Segoe UI, sans-serif",
        }}>
          {/* Header Section */}
          <div className="suite-section-heading" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: "Segoe UI, sans-serif",
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: "Segoe UI, sans-serif", }}>Payment & Receipts</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
          <div style={{
            marginBottom: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: "#0d766e" },
                      '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: "#0d766e" },
                      '&.Mui-focused fieldset': { borderColor: "#0d766e" },
                    },
                  }}
                />
              </div>

              {/* Search - Icon on mobile, always visible on desktop */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: "#0d766e",
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: "#0d766e",
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: "#0d766e",
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: "#0d766e",
                      },
                    },
                  }}
                />
              )}

              <Tooltip title="Update cheque status in bulk" arrow>
                <Button
                  variant="contained"
                  onClick={handleOpenBulkChequeStatusModal}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : 'auto',
                    width: 'auto'
                  }}
                >
                  <Payment style={{ fontSize: '18px' }} />
                  {!isMobile && (
                    <span style={{ fontSize: '12px' }}>
                      Cheque Status ({filteredData.filter(item => {
                        const chequePaymentMethodId = getChequePaymentMethodId();
                        return item.PaymentType === chequePaymentMethodId &&
                          item.IsActive &&
                          (item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1);
                      }).length})
                    </span>
                  )}
                </Button>
              </Tooltip>

              {/* <Tooltip title="Refresh data" arrow>
                <Button
                  variant="contained"
                  onClick={fetchAllData}
                  disabled={loading}
                  className="action-button"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : 'auto',
                    width: 'auto'
                  }}
                >
                  <Refresh style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Refresh</span>}
                </Button>
              </Tooltip> */}

              {hasCreatePermission && (
                <Tooltip title="Create a new payment/receipt" arrow>
                  <Button
                    variant="contained"
                    onClick={() => handleOpenModal('create')}
                    className="action-button"
                    style={{
                      background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                      color: 'white',
                      padding: isMobile ? '0' : '4px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '700',
                      boxShadow: "0 2px 8px rgba(13, 118, 110, 0.4)",
                      border: 'none',
                      height: '32px',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap',
                      minWidth: isMobile ? '32px' : '100px',
                      width: 'auto',
                      marginLeft: 'auto'
                    }}
                  >
                    <Add style={{ fontSize: '18px' }} />
                    {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="payment receipts table">
                <TableHead>
                  <TableRow style={{
                    background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                    height: '28px'
                  }}>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Voucher No
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Date
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Type
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Party
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Payment Type
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Amount
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'left',
                      height: '28px'
                    }}>
                      Status
                    </TableCell>
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                          No payment receipts found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item) => (
                      <TableRow
                        key={item.Id}
                        sx={{
                          '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          borderBottom: '1px solid #f1f5f9'
                        }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                            {item.BillNumber}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                            FY: {item.FinYear}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {formatDate(item.BillDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip
                            label={item.IsType ? 'Payment' : 'Receipt'}
                            sx={{
                              backgroundColor: item.IsType ? '#fee2e2' : '#d1fae5',
                              color: item.IsType ? '#dc2626' : '#065f46',
                              fontSize: '10px',
                              height: '20px',
                              borderRadius: '10px',
                              fontWeight: 500
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.3, fontSize: '0.75rem' }}>
                            {item.MasterName || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {getPaymentTypeText(item.PaymentType)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#059669' }}>
                            {formatCurrency(item.Amount)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip
                            label={item.IsActive ? 'Active' : 'Inactive'}
                            color={item.IsActive ? 'success' : 'default'}
                            size="small"
                            sx={{ fontSize: '10px', height: '20px' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1, textAlign: 'right' }}>
                          <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenModal('view', item)}
                                sx={{
                                  color: '#3b82f6',
                                  backgroundColor: '#eff6ff',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#dbeafe',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Visibility sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Download PDF">
                              <IconButton
                                size="small"
                                onClick={() => handlePrintPaymentReceipt(item.Id)}
                                sx={{
                                  color: item.IsType ? '#dc2626' : '#059669',
                                  backgroundColor: item.IsType ? '#fee2e2' : '#d1fae5',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: item.IsType ? '#fecaca' : '#a7f3d0',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Print sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>

                            {hasEditPermission && item.IsActive && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenModal('edit', item)}
                                  sx={{
                                    color: '#f59e0b',
                                    backgroundColor: '#fffbeb',
                                    width: 28,
                                    height: 28,
                                    '&:hover': {
                                      backgroundColor: '#fef3c7',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  <Edit sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {hasDeletePermission && (
                              <Tooltip title={item.IsActive ? 'Delete' : 'Activate'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (item.IsActive) {
                                      setDeleteItemId(item.Id);
                                      setDeleteItemNo(item.BillNumber);
                                      setDeleteModalOpen(true);
                                    } else {
                                      setActivateItemId(item.Id);
                                      setActivateItemNo(item.BillNumber);
                                      setActivateModalOpen(true);
                                    }
                                  }}
                                  sx={{
                                    color: item.IsActive ? '#ef4444' : '#10b981',
                                    backgroundColor: item.IsActive ? '#fef2f2' : '#d1fae5',
                                    width: 28,
                                    height: 28,
                                    '&:hover': {
                                      backgroundColor: item.IsActive ? '#fecaca' : '#a7f3d0',
                                      transform: 'scale(1.1)'
                                    }
                                  }}
                                >
                                  {item.IsActive ? <Delete sx={{ fontSize: 14 }} /> : <CheckCircle sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredData.length}
            itemsPerPage={rowsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newSize) => {
              setRowsPerPage(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Warning />
              Confirm Deletion
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <DialogContentText>
              Are you sure you want to delete voucher {deleteItemNo}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteModalOpen(false)} variant="outlined">Cancel</Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Dialog */}
        <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckCircle />
              Confirm Activation
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <DialogContentText>
              Are you sure you want to activate voucher {activateItemNo}?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setActivateModalOpen(false)} variant="outlined">Cancel</Button>
            <Button onClick={handleActivateConfirm} variant="contained" color="success">Activate</Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Cheque Status Update Modal */}
        <Dialog 
          open={bulkChequeStatusModalOpen} 
          onClose={() => {
            setBulkChequeStatusModalOpen(false);
            setIndividualChequeStatuses({});
          }} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Payment />
              Update Cheque Status
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
              Update the status of pending cheque payments below:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedChequeItems.map((item) => (
                <Box
                  key={item.Id}
                  sx={{
                    p: 2,
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.BillNumber} - {item.MasterName}
                    </Typography>
                    <Chip
                      label={formatCurrency(item.Amount)}
                      size="small"
                      sx={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
                    />
                  </Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={individualChequeStatuses[item.Id] || 1}
                      onChange={(e) => setIndividualChequeStatuses(prev => ({
                        ...prev,
                        [item.Id]: parseInt(e.target.value)
                      }))}
                      label="Status"
                    >
                      <MenuItem value={1}>Pending</MenuItem>
                      <MenuItem value={2}>Passed</MenuItem>
                      <MenuItem value={3}>Bounced</MenuItem>
                      <MenuItem value={4}>Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                setBulkChequeStatusModalOpen(false);
                setIndividualChequeStatuses({});
              }} 
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpdateChequeStatus} 
              variant="contained" 
              sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                }
              }}
            >
              Update All
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment/Receipt Modal */}
        <PaymentReceiptsModal
          open={openModal}
          onClose={handleCloseModal}
          onSave={handleSavePaymentReceipt}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          paymentReceiptDetails={paymentReceiptDetails}
          setPaymentReceiptDetails={setPaymentReceiptDetails}
          masters={masters}
          banks={banks}
          purchases={purchases}
          sales={sales}
          paymentMethods={paymentMethods}
          gstRates={gstRates}
          loading={modalLoading}
          companyId={companyId}
          token={token}
          branchId={branchId}
          fromDate={fromDate}
          toDate={toDate}
          fetchBillsForMaster={fetchBillsForMaster}
          fetchMaxBillNo={fetchMaxBillNo}
        />

        {/* Success/Error Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={3000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </div>
    </Fade>
  );
};

export default PaymentReceiptsIndex;
